<template>
  <div class="login-page">
    <!-- Background watermark -->
    <BrandMark class="login-watermark" />

    <!-- Login card -->
    <div class="login-card">
      <div class="login-logo">
        <BrandMark />
      </div>

      <form @submit.prevent="login()">
        <div class="form-group">
          <label for="txtEmail">E-mail</label>
          <div class="input-wrapper">
            <i class="fontello-icon">&#xf2be;</i>
            <input
              @focus="dismissError()"
              ref="email"
              type="email"
              name="txtEmail"
              id="txtEmail"
              placeholder="Enter your email"
              autocomplete="off"
              v-model="email"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="txtPassword">Password</label>
          <div class="input-wrapper">
            <i class="fontello-icon">&#xe80e;</i>
            <input
              @focus="dismissError()"
              ref="password"
              type="password"
              name="txtPassword"
              id="txtPassword"
              placeholder="Enter your password"
              autocomplete="nope"
              v-model="password"
            />
            <button
              type="button"
              class="toggle-password"
              @click="togglePassword"
              :title="isPasswordVisible ? 'Hide password' : 'Show password'"
            >
              <AppIcon v-if="isPasswordVisible" name="eye" :stroke="2" />
              <AppIcon v-else name="eye-off" :stroke="2" />
            </button>
          </div>
        </div>

        <!-- Inline error -->
        <transition name="error-slide">
          <div class="login-error" v-if="errorStatus" @click="dismissError">
            <AppIcon name="alert-circle" class="login-error-icon" :stroke="2" />
            <span class="login-error-text">{{ errorMessage }}</span>
            <button type="button" class="login-error-close" @click.stop="dismissError">
              <AppIcon name="x" :stroke="2" />
            </button>
          </div>
        </transition>

        <button
          type="submit"
          class="btn btn-primary login-btn"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting" class="btn-spinner"></span>
          {{ isSubmitting ? "Logging in..." : "Login" }}
        </button>
      </form>
    </div>

    <!-- Accent bar -->
    <div class="login-accent-bar"></div>
  </div>
</template>

<script>
import store from "@/store";
import Authorization from "@/store/modules/auth/authorization.js";
import { mapState } from "vuex";
export default {
  name: "Login",
  data() {
    return {
      email: "",
      password: "",
      errorStatus: false,
      errorMessage: "",
      isPasswordVisible: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState({
      stateAuth: "auth",
      stateCommon: "common",
    }),
  },
  mounted() {
    this.focusInput();
  },
  methods: {
    togglePassword() {
      this.isPasswordVisible = !this.isPasswordVisible;
      this.$refs.password.type = this.isPasswordVisible ? "text" : "password";
    },
    validateEmail(email) {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    async login() {
      if (this.email === "" || this.password === "") {
        this.errorStatus = true;
        this.errorMessage = "Please fill all fields!";
      } else {
        if (this.validateEmail(this.email)) {
          this.errorStatus = false;
        } else {
          this.errorStatus = true;
          this.errorMessage = "Invalid email format!";
          return;
        }
        this.isSubmitting = true;
        store.commit("LOADING", { isActiveStatus: true });
        let user = await Authorization({
          email: this.email,
          password: this.password,
        });
        if (user.status !== false) {
          localStorage.setItem("user", user.name);
          localStorage.setItem("token", user.token);
          localStorage.setItem("uhash", user.uhash);
          localStorage.setItem("utype", user.utype);
          localStorage.setItem("recipes_access", user.recipe_access);
          localStorage.setItem("invoices_access", user.invoice_access);
          localStorage.setItem("vendors_access", user.vendor_access);
          localStorage.setItem("stocks_access", user.stock_access);
          store.commit("LOGIN", {
            name: user.name,
            status: true,
            token: user.token,
            uhash: user.uhash,
            utype: user.utype,
            recipes_access: user.recipe_access,
            invoices_access: user.invoice_access,
            vendors_access: user.vendor_access,
            stocks_access: user.stock_access,
          });
          store.commit("LOADING", { isActiveStatus: false });
          this.$router.push("/").catch(err => {
            if (err.name !== 'NavigationDuplicated') throw err;
          });
        } else {
          store.commit("LOADING", { isActiveStatus: false });
          this.isSubmitting = false;
          this.errorStatus = true;
          this.errorMessage = user.message;
        }
      }
    },
    dismissError() {
      this.errorStatus = false;
    },
    focusInput() {
      this.$refs.email.focus();
    },
  },
};
</script>

<style scoped>
/* Page */
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-secondary);
  position: relative;
  overflow: hidden;
}

/* Watermark */
.login-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60vw;
  max-width: 600px;
  pointer-events: none;
  z-index: 0;
}

/* Card */
.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 26rem;
  padding: 2.5rem 2rem;
  background-color: var(--bg-primary);
  border-radius: var(--card-radius, 12px);
  border: 1px solid var(--border-primary);
  box-shadow: var(--card-shadow, var(--shadow-lg));
  animation: fadeInUp 0.4s ease-out;
}

/* Logo */
.login-logo {
  margin: 0 auto 2rem;
  width: 8rem;
}
.login-logo img,
.login-logo svg {
  width: 100%;
  height: auto;
  display: block;
}
/* A vector mark fades through `color` + fill="currentColor"; a bitmap mark
   has no fill to set, so it fades through opacity. BrandMark puts the class on
   the svg or the img itself, hence the element-qualified selectors. */
svg.login-watermark {
  color: var(--brand-watermark);
}
img.login-watermark {
  opacity: var(--brand-watermark-opacity);
}

/* Form */
.form-group {
  margin-bottom: 1.25rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.375rem;
  font-size: var(--t-body);
  font-weight: 500;
  color: var(--form-label);
}
.input-wrapper {
  position: relative;
}
.input-wrapper i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25em;
  color: var(--text-tertiary);
  pointer-events: none;
  z-index: 1;
}
.input-wrapper input {
  padding-left: 2.75rem;
  width: 100%;
}
.input-wrapper input {
  height: 46px;
  border-radius: 10px;
  border: 1.5px solid var(--border-primary);
  background: var(--bg-input);
  transition: var(--transition-fast, all 0.15s ease);
}
.input-wrapper input:focus {
  border-color: var(--border-input-focus);
  background: var(--bg-primary);
  box-shadow: 0 0 0 3px var(--brand-orange-glow, rgba(249, 115, 22, 0.15));
}

/* Password toggle */
.toggle-password {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}
.toggle-password:hover {
  color: var(--text-tertiary);
}
.toggle-password svg {
  width: 1.25rem;
  height: 1.25rem;
}

/* Login button */
.login-btn {
  width: 100%;
  height: 2.75rem;
  font-size: var(--t-body);
  font-weight: 600;
  letter-spacing: 0.025em;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 10px;
  background: var(--gradient-accent, #f97316);
  color: #ffffff;
  border: none;
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.35);
  cursor: pointer;
  transition: var(--transition-fast, all 0.15s ease);
}
.login-btn:hover:not(:disabled) {
  background: var(--brand-orange-dark, #c2410c);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4);
  transform: translateY(-1px);
}
.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.btn-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--btn-primary-hover-text);
  border-top-color: transparent;
  border-radius: 9999px;
  animation: spinner 0.6s infinite linear;
}

/* Footer */
.login-footer {
  margin-top: 2rem;
  text-align: center;
  font-size: var(--t-body);
  font-weight: 500;
  color: var(--text-muted);
  position: relative;
  z-index: 1;
}

/* Accent bar */
.login-accent-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 0.25rem;
  background: var(--gradient-accent, var(--accent-highlight));
}

/* Inline error */
.login-error {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.25rem;
  background-color: var(--status-error-light);
  border: 1px solid var(--status-error);
  border-radius: 0.5rem;
  color: var(--status-error);
  cursor: pointer;
}
.login-error-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}
.login-error-text {
  flex: 1;
  font-size: var(--t-body);
  font-weight: 500;
  line-height: 1.25rem;
}
.login-error-close {
  background: none;
  border: none;
  color: var(--status-error);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  flex-shrink: 0;
}
.login-error-close:hover {
  opacity: 1;
}
.login-error-close svg {
  width: 1rem;
  height: 1rem;
}

/* Error slide transition */
.error-slide-enter-active {
  animation: errorSlideIn 0.3s ease-out;
}
.error-slide-leave-active {
  animation: errorSlideIn 0.2s ease-in reverse;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes spinner {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(359deg);
  }
}
@keyframes errorSlideIn {
  from {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
